import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LaunchIcon from '@mui/icons-material/Launch';
import { bugsApi } from '../../api/acceptanceApi';
import { getApiErrorMessage } from '../../api/client';
import { notify } from '../../components/Notifier';
import { usePermission } from '../../auth/usePermission';
import { ConfirmDialog } from '../Admin/ConfirmDialog';
import { formatDateTime } from '../../utils/date';
import { BUG_STATUS_META, BUG_STATUS_OPTIONS, isBugOpen } from './statusMeta';
import { BUG_SEVERITY_META, BUG_SEVERITY_OPTIONS } from './severityMeta';
import { BugFormDialog } from './BugFormDialog';
import { BugImageGallery } from './BugImageGallery';
import { BugCommentThread } from './BugCommentThread';
import { bugSourceLabel } from './sourceMeta';
import { useProjects } from '../../context/ProjectContext';
import { TicketCodeBadge } from '../../components/TicketCodeBadge';
import type { Bug, BugSeverity, BugStatus } from '../../types';

interface BugDetailDialogProps {
  bug: Bug;
  onClose: () => void;
  /** Gọi sau khi bug được sửa / đổi trạng thái / xoá (để danh sách refresh). */
  onChanged: () => void;
  /** Ẩn nút "Mở tính năng" khi đang đứng sẵn ở màn chi tiết tính năng đó. */
  hideFeatureLink?: boolean;
}

/** Nhãn dòng thời gian khớp đúng nghĩa từng trạng thái — "Đóng lúc" chỉ còn
 * đúng cho CLOSED, không dùng chung cho mọi trạng thái không-mở nữa. */
function resolvedAtLabel(status: BugStatus): string {
  switch (status) {
    case 'RESOLVED':
    case 'VERIFIED':
      return 'Đã sửa lúc';
    case 'REJECTED':
      return 'Đánh dấu không phải lỗi lúc';
    case 'CLOSED':
      return 'Đóng lúc';
    default:
      return 'Đóng lúc';
  }
}

/** Dialog chi tiết một bug. Khách hàng chỉ xem; BUG_UPDATE/BUG_DELETE có thêm thao tác. */
export function BugDetailDialog({ bug: initialBug, onClose, onChanged, hideFeatureLink }: BugDetailDialogProps): JSX.Element {
  const navigate = useNavigate();
  const { projects } = useProjects();
  const canUpdate = usePermission('BUG_UPDATE');
  const canDelete = usePermission('BUG_DELETE');
  const canComment = usePermission('BUG_COMMENT');
  const canVerify = usePermission('BUG_VERIFY');
  const canHide = usePermission('BUG_HIDE');
  const canEditSeverity = usePermission('BUG_SEVERITY_EDIT');
  const [bug, setBug] = useState<Bug>(initialBug);
  const [updating, setUpdating] = useState<BugStatus | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [savingSeverity, setSavingSeverity] = useState(false);
  const [hiding, setHiding] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [reopening, setReopening] = useState(false);
  /** Tăng để khu Trao đổi nạp lại sau khi lý do bác bỏ được ghi thành bình luận. */
  const [commentsReloadKey, setCommentsReloadKey] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  /** Tăng sau khi sửa bug để gallery nạp lại (ảnh có thể vừa thêm/xoá trong form). */
  const [imagesReloadKey, setImagesReloadKey] = useState(0);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const meta = BUG_STATUS_META[bug.status];

  const changeStatus = async (status: BugStatus) => {
    setUpdating(status);
    try {
      const updated = await bugsApi.update(bug.id, { status });
      setBug(updated);
      notify(`Đã chuyển lỗi sang "${BUG_STATUS_META[status].label}"`, 'success');
      onChanged();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể cập nhật trạng thái'), 'error');
    } finally {
      setUpdating(null);
    }
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const updated = await bugsApi.verify(bug.id);
      setBug(updated);
      notify('Đã xác minh lỗi không còn xuất hiện', 'success');
      onChanged();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể xác minh lỗi'), 'error');
    } finally {
      setVerifying(false);
    }
  };

  const changeSeverity = async (value: BugSeverity | '') => {
    setSavingSeverity(true);
    try {
      const updated = await bugsApi.updateSeverity(bug.id, value || null);
      setBug(updated);
      notify('Đã cập nhật mức độ nghiêm trọng', 'success');
      onChanged();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể cập nhật mức độ nghiêm trọng'), 'error');
    } finally {
      setSavingSeverity(false);
    }
  };

  const reopen = async () => {
    setReopening(true);
    try {
      const updated = await bugsApi.reopen(bug.id, reopenReason.trim());
      setBug(updated);
      setReopenOpen(false);
      setReopenReason('');
      // Lý do vừa được backend ghi thành bình luận — nạp lại khu Trao đổi.
      setCommentsReloadKey((k) => k + 1);
      notify('Đã báo lỗi vẫn còn', 'success');
      onChanged();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể báo lỗi vẫn còn'), 'error');
    } finally {
      setReopening(false);
    }
  };

  const toggleHide = async () => {
    setHiding(true);
    try {
      const updated = bug.is_hidden === 1 ? await bugsApi.unhide(bug.id) : await bugsApi.hide(bug.id);
      setBug(updated);
      notify(updated.is_hidden === 1 ? 'Đã ẩn lỗi' : 'Đã hiện lại lỗi', 'success');
      onChanged();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể ẩn/hiện lỗi'), 'error');
    } finally {
      setHiding(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await bugsApi.remove(bug.id);
      notify('Đã xoá lỗi', 'success');
      setDeleteOpen(false);
      onChanged();
      onClose();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể xoá lỗi'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const goToFeature = () => {
    // KHÔNG gọi onClose() ở đây: điều hướng rời khỏi /bugs/* đã tự unmount
    // trang Bugs (và dialog này theo) rồi. Gọi thêm onClose() sau navigate()
    // từng vô tình chạy đua với onClose của Bugs.tsx (giờ tự `navigate('/bugs')`
    // cho cơ chế deep-link mã ticket) — ai gọi navigate() sau thắng, khiến
    // "Mở tính năng" bị kéo ngược lại /bugs thay vì sang /projects/...
    navigate(`/projects/${bug.project_id}?phaseId=${bug.phase_id ?? ''}&featureId=${bug.feature_id}`);
  };

  return (
    <>
      <Dialog open onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pr: 6 }}>
          {bug.title}
          <IconButton aria-label="Đóng" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <TicketCodeBadge ticketCode={bug.ticket_code} path={`/bugs/${bug.ticket_code ?? bug.id}`} />
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Chip
                label={meta.label}
                color={meta.color === 'default' ? 'default' : meta.color}
                size="small"
                variant="outlined"
              />
              {bug.severity && (
                <Chip
                  label={BUG_SEVERITY_META[bug.severity].label}
                  color={BUG_SEVERITY_META[bug.severity].color === 'default' ? 'default' : BUG_SEVERITY_META[bug.severity].color}
                  size="small"
                  variant="outlined"
                />
              )}
              <Chip
                label={bugSourceLabel(bug.source, projects.find((p) => p.id === bug.project_id)?.customer_name)}
                size="small"
                variant="outlined"
              />
              {bug.is_hidden === 1 && <Chip label="Đã ẩn" size="small" color="default" variant="outlined" />}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {bug.phase_name ? `${bug.phase_name} › ` : ''}
              {bug.feature_name ?? 'Tính năng'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Tạo bởi {bug.created_by_name ?? 'không rõ'} lúc {formatDateTime(bug.created_at)}
              {bug.resolved_at ? ` · ${resolvedAtLabel(bug.status)} ${formatDateTime(bug.resolved_at)}` : ''}
              {bug.discovered_at ? ` · Phát hiện lúc ${formatDateTime(bug.discovered_at)}` : ''}
            </Typography>
            {bug.verified_at && (
              <Typography variant="caption" color="text.secondary">
                Xác minh lúc {formatDateTime(bug.verified_at)} bởi {bug.verified_by_name ?? 'không rõ'}
              </Typography>
            )}
            {bug.discovered_note && (
              <Typography variant="caption" color="text.secondary">
                Ghi chú người phát hiện: {bug.discovered_note}
              </Typography>
            )}
            <Divider />
            <Typography variant="subtitle2" fontWeight={700}>
              Mô tả chi tiết
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }} color={bug.description ? 'text.primary' : 'text.secondary'}>
              {bug.description || 'Chưa có mô tả.'}
            </Typography>

            <BugImageGallery bugId={bug.id} canEdit={canUpdate} reloadKey={imagesReloadKey} />

            {canUpdate && (
              <>
                <Divider />
                <Typography variant="subtitle2" fontWeight={700}>
                  Chuyển trạng thái
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {BUG_STATUS_OPTIONS.filter((s) => s !== bug.status).map((s) => (
                    <Button
                      key={s}
                      size="small"
                      variant="outlined"
                      color={BUG_STATUS_META[s].color === 'default' ? 'inherit' : BUG_STATUS_META[s].color}
                      disabled={updating !== null}
                      onClick={() => void changeStatus(s)}
                    >
                      {updating === s ? 'Đang cập nhật…' : BUG_STATUS_META[s].label}
                    </Button>
                  ))}
                </Stack>
              </>
            )}

            {/*
              Chọn mức độ nghiêm trọng riêng cho người CHỈ có BUG_SEVERITY_EDIT
              (vd Customer được cấp quyền này): người có BUG_UPDATE đã sửa được
              severity qua form "Sửa" phía dưới nên không hiện lại ở đây.
            */}
            {canEditSeverity && !canUpdate && (
              <>
                <Divider />
                <TextField
                  select
                  label="Mức độ nghiêm trọng"
                  value={bug.severity ?? ''}
                  onChange={(e) => void changeSeverity(e.target.value as BugSeverity | '')}
                  size="small"
                  disabled={savingSeverity}
                  sx={{ maxWidth: 240 }}
                  SelectProps={{ displayEmpty: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">Chưa phân loại</MenuItem>
                  {BUG_SEVERITY_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s}>
                      {BUG_SEVERITY_META[s].label}
                    </MenuItem>
                  ))}
                </TextField>
              </>
            )}

            {/*
              Nút xác minh riêng cho người CHỈ có BUG_VERIFY (khách hàng): người
              có BUG_UPDATE đã thấy "Đã xác minh" trong khối chuyển trạng thái
              phía trên nên không hiện lại.
            */}
            {canVerify && !canUpdate && (
              <>
                <Divider />
                <Typography variant="subtitle2" fontWeight={700}>
                  Xác nhận kết quả xử lý
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {bug.status !== 'VERIFIED' && (
                    <Button
                      variant="outlined"
                      color="info"
                      size="small"
                      disabled={verifying}
                      onClick={() => void verify()}
                    >
                      {verifying ? 'Đang xác minh…' : 'Đã xác minh — lỗi không còn'}
                    </Button>
                  )}
                  {bug.status !== 'REOPENED' && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      disabled={verifying}
                      onClick={() => setReopenOpen(true)}
                    >
                      Lỗi vẫn còn
                    </Button>
                  )}
                </Stack>
              </>
            )}

            <Divider />
            <BugCommentThread bugId={bug.id} canComment={canComment} reloadKey={commentsReloadKey} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Stack direction="row" spacing={1}>
            {canDelete && (
              <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={() => setDeleteOpen(true)}>
                Xoá
              </Button>
            )}
            {canUpdate && (
              <Button startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>
                Sửa
              </Button>
            )}
            {/* Chỉ ẩn được bug source=TEAM đã đóng — bug khách báo luôn hiển thị. */}
            {canHide && bug.source === 'TEAM' && (bug.is_hidden === 1 || !isBugOpen(bug.status)) && (
              <Button disabled={hiding} onClick={() => void toggleHide()}>
                {hiding ? 'Đang xử lý…' : bug.is_hidden === 1 ? 'Hiện lại' : 'Ẩn'}
              </Button>
            )}
          </Stack>
          {!hideFeatureLink && (
            <Button startIcon={<LaunchIcon />} onClick={goToFeature}>
              Mở tính năng
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <BugFormDialog
        open={editOpen}
        featureId={bug.feature_id}
        featureName={bug.feature_name ?? undefined}
        bug={bug}
        onClose={() => setEditOpen(false)}
        onSaved={(saved) => {
          setBug(saved);
          setEditOpen(false);
          setImagesReloadKey((k) => k + 1);
          onChanged();
        }}
      />

      {/*
        Lý do là bắt buộc (backend trả 400 nếu rỗng): đội xử lý cần biết còn sai
        ở đâu thay vì chỉ thấy bug bật lại trạng thái mở.
      */}
      <Dialog open={reopenOpen} onClose={() => setReopenOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Lỗi vẫn còn</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Vui lòng nhập mô tả vị trí lỗi sai"
            placeholder="Ví dụ: vẫn hiện sai tên danh mục ở bước 3."
            value={reopenReason}
            onChange={(e) => setReopenReason(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            margin="dense"
            disabled={reopening}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReopenOpen(false)} disabled={reopening}>
            Huỷ
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={reopening || reopenReason.trim() === ''}
            onClick={() => void reopen()}
          >
            {reopening ? 'Đang gửi…' : 'Gửi'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteOpen}
        title="Xoá lỗi"
        message={`Xoá lỗi "${bug.title}"?`}
        confirmLabel="Xoá"
        loading={deleting}
        onConfirm={() => void handleDelete()}
        onCancel={() => setDeleteOpen(false)}
      />
    </>
  );
}
